import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { CRM } from '@/features/crm/types';
import useBroker from '@/hooks/useBroker';
import hasItems from '@/lib/utils/hasItems';
import isEmpty from '@/lib/utils/isEmpty';

import {
  type CrmRequirableField,
  REQUIRED_FIELDS_BY_CRM,
} from './requiredFieldsByCrm';
import type { CreatePartyValues } from './types';

export default function useRequiredByCrm() {
  const { t, i18n } = useTranslation();
  const { crms } = useBroker();

  const exportToCrm = useWatch<CreatePartyValues, 'exportToCrm'>({
    name: 'exportToCrm',
  });

  const getRequiringCrmNames = (
    field: CrmRequirableField,
    selectedCrms: Maybe<Partial<Record<CRM, boolean>>>,
  ) =>
    (crms ?? [])
      .filter((crm) => {
        const crmKey = crm.key as CRM;
        const isSelected = selectedCrms?.[crmKey] === true;
        const requiresField =
          REQUIRED_FIELDS_BY_CRM[crmKey]?.includes(field) === true;

        return isSelected && requiresField;
      })
      .map((crm) => crm.name ?? crm.key);

  return (field: CrmRequirableField, fieldLabel: string) => ({
    isRequired: hasItems(getRequiringCrmNames(field, exportToCrm)),

    validate: (value: Maybe<string>, formValues: CreatePartyValues) => {
      const requiringCrmNames = getRequiringCrmNames(
        field,
        formValues.exportToCrm,
      );
      if (!isEmpty(value) || !hasItems(requiringCrmNames)) return true;

      const crmNames = new Intl.ListFormat(i18n.language, {
        type: 'conjunction',
      }).format(requiringCrmNames);

      return t('PARTY_CREATE_INLINE_REQUIRED_FOR_CRM', {
        field: fieldLabel,
        crm: crmNames,
      });
    },
  });
}
